import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const MapContainer = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f0f0;
  position: relative;
  
  @media (min-width: 768px) {
    height: 250px;
  }
`;

const MapFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: #f0f0f0;
`;

const MapPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  color: #666;
  text-align: center;
  padding: 20px;
  
  .map-icon {
    font-size: 48px;
    margin-bottom: 12px;
  }
  
  .map-text {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 8px;
  }
  
  .map-address {
    font-size: 14px;
    color: #999;
    line-height: 1.4;
  }
`;

const ErrorState = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff3cd;
  color: #856404;
  text-align: center;
  padding: 20px;
  
  .error-icon {
    font-size: 32px;
    margin-bottom: 8px;
  }
  
  .error-text {
    font-size: 14px;
    line-height: 1.4;
  }
`;

const MapOverlay = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.95);
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  color: #333;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  .overlay-text {
    font-weight: 500;
    margin-bottom: 2px;
  }
  
  .overlay-note {
    font-size: 11px;
    color: #666;
  }
`;

const PropertyMap = ({ 
  property, 
  showOverlay = true, 
  height = "200px",
  className = "" 
}) => {
  const [mapSrc, setMapSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (property && property.address_line1) {
      generateMapSrc();
    }
  }, [property]);

  const generateMapSrc = () => {
    try {
      // Build full address for geocoding
      const addressComponents = [
        property.address_line1,
        property.unit_number,
        property.city,
        property.state,
        property.zip_code
      ].filter(Boolean); // Remove empty values
      
      const fullAddress = addressComponents.join(', ');
      
      // For privacy and security, we'll show the general area (street level) not the exact address
      const generalAddress = [
        property.address_line1.split(' ').slice(0, -1).join(' '), // Remove house number for privacy
        property.city,
        property.state,
        property.zip_code
      ].filter(Boolean).join(', ');
      
      // Encode address for URL
      const encodedAddress = encodeURIComponent(fullAddress);
      const encodedGeneralAddress = encodeURIComponent(generalAddress);
      
      // Use Google Maps Embed API
      // Note: In production, you'll need a Google Maps API key
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      if (apiKey) {
        // Use Google Maps Embed API with API key
        const embedSrc = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodedAddress}&zoom=15&maptype=roadmap`;
        setMapSrc(embedSrc);
      } else {
        // Fallback to Google Maps in iframe (less reliable but works without API key)
        const fallbackSrc = `https://maps.google.com/maps?q=${encodedGeneralAddress}&output=embed&z=15`;
        setMapSrc(fallbackSrc);
      }
      
      setLoading(false);
      setError(false);
      
    } catch (err) {
      console.error('Error generating map:', err);
      setError(true);
      setLoading(false);
    }
  };

  const handleMapError = () => {
    setError(true);
    setLoading(false);
  };

  const getDisplayAddress = () => {
    if (!property) return '';
    
    const components = [
      property.address_line1,
      property.unit_number,
      property.city,
      property.state,
      property.zip_code
    ].filter(Boolean);
    
    return components.join(', ');
  };

  const getGeneralLocation = () => {
    if (!property) return '';
    
    // Show general area for privacy
    return `${property.city}, ${property.state} ${property.zip_code}`;
  };

  if (!property || !property.address_line1) {
    return (
      <MapContainer style={{ height }} className={className}>
        <MapPlaceholder>
          <div className="map-icon">📍</div>
          <div className="map-text">Location Map</div>
          <div className="map-address">Address not available</div>
        </MapPlaceholder>
      </MapContainer>
    );
  }

  if (loading) {
    return (
      <MapContainer style={{ height }} className={className}>
        <MapPlaceholder>
          <div className="map-icon">🗺️</div>
          <div className="map-text">Loading Map...</div>
          <div className="map-address">{getGeneralLocation()}</div>
        </MapPlaceholder>
      </MapContainer>
    );
  }

  if (error || !mapSrc) {
    return (
      <MapContainer style={{ height }} className={className}>
        <ErrorState>
          <div className="error-icon">⚠️</div>
          <div className="error-text">
            Map temporarily unavailable<br/>
            <strong>{getGeneralLocation()}</strong>
          </div>
        </ErrorState>
      </MapContainer>
    );
  }

  return (
    <MapContainer style={{ height }} className={className}>
      <MapFrame
        src={mapSrc}
        title={`Map of ${getDisplayAddress()}`}
        loading="lazy"
        onError={handleMapError}
        referrerPolicy="no-referrer-when-downgrade"
      />
      
      {showOverlay && (
        <MapOverlay>
          <div className="overlay-text">📍 Property Location</div>
          <div className="overlay-note">
            General area shown for privacy
          </div>
        </MapOverlay>
      )}
    </MapContainer>
  );
};

export default PropertyMap;